import { useEffect, useMemo, useState } from 'react'

import { useNicheMutations } from 'hooks-queries/niche'
import { useBrandTerritories } from 'modules/brands/common/hooks'
import { SYSTEM_CONTEXT_IDS } from 'modules/niche/common/niche.consts'
import { FeatureFlagsStore } from 'stores'

import { CREATE_NICHE_CONTEXT_HASH_LOG } from './use-brand-niche-hash.consts'
import type { IUseBrandNicheHashProps, IUseBrandNicheHashReturn } from './use-brand-niche-hash.types'

export const useBrandNicheHash = ({
  territoryId,
  selectedContentType,
  contextId,
  skip = false,
}: IUseBrandNicheHashProps): IUseBrandNicheHashReturn => {
  const [hashByTerritory, setHashByTerritory] = useState<Record<string, string>>({})
  const [hashError, setHashError] = useState(false)

  const resolvedContextId = contextId ?? SYSTEM_CONTEXT_IDS.mostEngaged

  const isNewPageBrandOverviewEnabled = FeatureFlagsStore.isFeatureFlagEnabled('isNewPageBrandOverviewEnabled')

  const brandTerritories = useBrandTerritories({
    currentTerritoryId: territoryId,
    brandContentType: selectedContentType,
    skipQuery: isNewPageBrandOverviewEnabled || skip,
  })

  const targetTerritoryId = useMemo(() => {
    if (skip) return null
    if (isNewPageBrandOverviewEnabled) return territoryId

    const territories = brandTerritories?.territories
    if (!territories) return null

    if (selectedContentType === 'total') {
      return territories.earned
    }
    return territories[selectedContentType]
  }, [skip, isNewPageBrandOverviewEnabled, territoryId, brandTerritories?.territories, selectedContentType])

  const [createNicheContextHash] = useNicheMutations({
    mutationName: 'createNicheContextHash',
    errorLog: CREATE_NICHE_CONTEXT_HASH_LOG,
  })

  const cacheKey = targetTerritoryId ? `${targetTerritoryId}:${resolvedContextId}` : null
  const contextHash = cacheKey ? hashByTerritory[cacheKey] : null

  useEffect(() => {
    setHashError(false)
  }, [targetTerritoryId, resolvedContextId])

  useEffect(() => {
    if (!targetTerritoryId || !cacheKey || contextHash || hashError) return

    createNicheContextHash({
      variables: { input: { contextId: resolvedContextId, territoryId: targetTerritoryId } },
      onCompleted: (data: { createNicheContextHash?: { hash?: string } }) => {
        const hash = data?.createNicheContextHash?.hash
        if (hash) {
          setHashByTerritory(prev => ({ ...prev, [cacheKey]: hash }))
        }
      },
      onError: () => setHashError(true),
    })
  }, [targetTerritoryId, cacheKey, contextHash, hashError, createNicheContextHash, resolvedContextId])

  // When skipped there is nothing to resolve, so the hook must not report a perpetual loading state.
  const isLoading = !skip && (!targetTerritoryId || (!contextHash && !hashError))

  return {
    contextHash,
    hashError,
    targetTerritoryId,
    isLoading,
  }
}
